import { FirebaseAnalytics } from "@capacitor-firebase/analytics";
import { CURRENT_ENV } from "./api";

/**
 * Check if analytics should be enabled (production only)
 */
const isAnalyticsEnabled = (): boolean => {
  return CURRENT_ENV.includes("production");
};

/**
 * Firebase Analytics service for user behavior tracking
 */
class AnalyticsService {
  private initialized = false;

  /**
   * Initialize Firebase Analytics
   * Call this once in App.tsx on mount
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      console.log("[Analytics] Already initialized");
      return;
    }

    if (!isAnalyticsEnabled()) {
      console.log(
        "[Analytics Debug] Firebase Analytics disabled in non-production environment"
      );
      return;
    }

    try {
      // Enable analytics collection
      await FirebaseAnalytics.setEnabled({ enabled: true });
      await FirebaseAnalytics.setSessionTimeoutDuration({ duration: 900000 }); // 15 minutes

      this.initialized = true;
      console.log("✅ Firebase Analytics initialized successfully");
    } catch (error) {
      console.error("❌ Failed to initialize Firebase Analytics:", error);
    }
  }

  /**
   * Set user ID for analytics
   * @param userId - Unique user identifier
   */
  async setUserId(userId: string): Promise<void> {
    if (!isAnalyticsEnabled()) {
      console.log(`[Analytics Debug] User ID set: ${userId}`);
      return;
    }

    try {
      await FirebaseAnalytics.setUserId({ userId });
      console.log(`[Analytics] User ID set: ${userId}`);
    } catch (error) {
      console.error("[Analytics] Error setting user ID:", error);
    }
  }

  /**
   * Set user properties
   * @param properties - User properties as key-value pairs
   */
  async setUserProperties(properties: Record<string, string>): Promise<void> {
    if (!isAnalyticsEnabled()) {
      console.log("[Analytics Debug] User properties:", properties);
      return;
    }

    try {
      for (const [key, value] of Object.entries(properties)) {
        await FirebaseAnalytics.setUserProperty({
          key,
          value: String(value),
        });
      }
      console.log("[Analytics] User properties set");
    } catch (error) {
      console.error("[Analytics] Error setting user properties:", error);
    }
  }

  /**
   * Log screen view event
   * @param screenName - Name of the screen
   * @param screenClass - Optional screen class/component name
   */
  async logScreenView(screenName: string, screenClass?: string): Promise<void> {
    if (!isAnalyticsEnabled()) {
      console.log(`[Analytics Debug] Screen view: ${screenName}`);
      return;
    }

    try {
      await FirebaseAnalytics.setCurrentScreen({
        screenName,
      });

      await FirebaseAnalytics.logEvent({
        name: "screen_view",
        params: {
          screen_name: screenName,
          screen_class: screenClass || screenName,
        },
      });
      console.log(`[Analytics] Screen view logged: ${screenName}`);
    } catch (error) {
      console.error(
        `[Analytics] Error logging screen view ${screenName}:`,
        error
      );
    }
  }

  /**
   * Log custom event
   * @param eventName - Name of the event (use snake_case)
   * @param params - Optional event parameters
   */
  async logEvent(
    eventName: string,
    params?: Record<string, string | number | boolean>
  ): Promise<void> {
    if (!isAnalyticsEnabled()) {
      console.log(`[Analytics Debug] Event: ${eventName}`, params);
      return;
    }

    try {
      await FirebaseAnalytics.logEvent({
        name: eventName,
        params: params || {},
      });
      console.log(`[Analytics] Event logged: ${eventName}`, params);
    } catch (error) {
      console.error(`[Analytics] Error logging event ${eventName}:`, error);
    }
  }

  /**
   * Log button/element click
   * @param elementName - Name of the element clicked
   * @param screenName - Screen where click occurred
   * @param additionalParams - Optional additional parameters
   */
  async logClick(
    elementName: string,
    screenName: string,
    additionalParams?: Record<string, string | number>
  ): Promise<void> {
    await this.logEvent("element_clicked", {
      element_name: elementName,
      screen_name: screenName,
      timestamp: Date.now(),
      ...additionalParams,
    });
  }

  /**
   * Log user login
   * @param method - Login method (email, phone, google, apple, etc.)
   */
  async logLogin(method: string): Promise<void> {
    await this.logEvent("login", {
      method,
    });
  }

  /**
   * Log user signup
   * @param method - Signup method (email, phone, google, apple, etc.)
   */
  async logSignUp(method: string): Promise<void> {
    await this.logEvent("sign_up", {
      method,
    });
  }

  /**
   * Log app open
   */
  async logAppOpen(): Promise<void> {
    await this.logEvent("app_open");
  }

  /**
   * Log search
   * @param searchTerm - What user searched for
   */
  async logSearch(searchTerm: string): Promise<void> {
    await this.logEvent("search", {
      search_term: searchTerm,
    });
  }

  /**
   * Log share event
   * @param contentType - Type of content shared
   * @param itemId - ID of item shared
   */
  async logShare(contentType: string, itemId?: string): Promise<void> {
    await this.logEvent("share", {
      content_type: contentType,
      item_id: itemId || "",
    });
  }

  /**
   * Log tutorial begin
   */
  async logTutorialBegin(): Promise<void> {
    await this.logEvent("tutorial_begin");
  }

  /**
   * Log tutorial complete
   */
  async logTutorialComplete(): Promise<void> {
    await this.logEvent("tutorial_complete");
  }

  /**
   * Log Bluetooth device connection
   * @param deviceType - Type of device
   * @param success - Whether connection was successful
   */
  async logDeviceConnection(
    deviceType: string,
    success: boolean
  ): Promise<void> {
    await this.logEvent("device_connection", {
      device_type: deviceType,
      success,
    });
  }

  /**
   * Log therapy session
   * @param sessionType - Type of therapy session
   * @param duration - Session duration in seconds
   * @param completed - Whether session was completed
   */
  async logTherapySession(
    sessionType: string,
    duration: number,
    completed: boolean
  ): Promise<void> {
    await this.logEvent("therapy_session", {
      session_type: sessionType,
      duration,
      completed,
    });
  }

  /**
   * Log health data sync
   * @param dataType - Type of health data (steps, heart_rate, etc.)
   * @param recordCount - Number of records synced
   */
  async logHealthDataSync(
    dataType: string,
    recordCount: number
  ): Promise<void> {
    await this.logEvent("health_data_sync", {
      data_type: dataType,
      record_count: recordCount,
    });
  }

  /**
   * Log error/exception
   * @param description - Error description
   * @param fatal - Whether error was fatal
   */
  async logError(description: string, fatal = false): Promise<void> {
    await this.logEvent("error_occurred", {
      error_description: description,
      fatal,
    });
  }

  /**
   * Log a generic event
   * @param eventName - Name of the event
   * @param params - Optional event parameters
   */
  async logGenericEvent(
    eventName: string,
    params?: Record<string, string | number | boolean>
  ): Promise<void> {
    await this.logEvent(eventName, params);
  }

  /**
   * Enable/disable analytics collection
   * @param enabled - Whether to enable analytics
   */
  async setEnabled(enabled: boolean): Promise<void> {
    try {
      await FirebaseAnalytics.setEnabled({ enabled });
      console.log(`[Analytics] Collection ${enabled ? "enabled" : "disabled"}`);
    } catch (error) {
      console.error("[Analytics] Error toggling collection:", error);
    }
  }

  /**
   * Reset analytics data (for logout/new user)
   */
  async reset(): Promise<void> {
    if (!isAnalyticsEnabled()) {
      console.log("[Analytics Debug] Analytics reset");
      return;
    }

    try {
      await FirebaseAnalytics.resetAnalyticsData();
      console.log("[Analytics] Analytics data reset");
    } catch (error) {
      console.error("[Analytics] Error resetting analytics:", error);
    }
  }
}

// Export singleton instance
export default new AnalyticsService();
